/**
 * Node Type Registry Hook
 * =======================
 * 
 * Fetches and caches node type definitions from the backend.
 * Provides utilities for rendering node config forms based on registry.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../../../config';

// Cache for node types to avoid repeated fetches
let nodeTypeCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch and use node type registry
 */
export const useNodeTypeRegistry = () => {
  const [nodeTypes, setNodeTypes] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRegistry = useCallback(async (force = false) => {
    // Check cache first
    if (!force && nodeTypeCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_TTL)) {
      setNodeTypes(nodeTypeCache.node_types);
      setCategories(nodeTypeCache.categories);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/node-types`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Update cache
        nodeTypeCache = data;
        cacheTimestamp = Date.now();
        
        setNodeTypes(data.node_types);
        setCategories(data.categories);
        setError(null);
      } else {
        throw new Error('Failed to fetch node types');
      }
    } catch (err) {
      console.error('Error fetching node type registry:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
  }, [fetchRegistry]);

  /**
   * Get definition for a specific node type
   */
  const getNodeType = useCallback((typeId) => {
    if (!nodeTypes) return null;
    return nodeTypes[typeId] || null;
  }, [nodeTypes]);

  /**
   * Get simple fields for a node type (shown in Setup tab)
   */
  const getSimpleFields = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    if (!nodeDef) return ['label', 'enabled'];
    return nodeDef.simple_fields || ['label', 'enabled'];
  }, [getNodeType]);

  /**
   * Get advanced fields for a node type (shown in Advanced tab)
   */
  const getAdvancedFields = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    if (!nodeDef) return ['description', 'retry', 'timeout_ms', 'continue_on_error'];
    return nodeDef.advanced_fields || ['description', 'retry', 'timeout_ms', 'continue_on_error'];
  }, [getNodeType]);

  /**
   * Get params schema for a node type
   */
  const getParamsSchema = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    if (!nodeDef) return {};
    return nodeDef.params_schema || {};
  }, [getNodeType]);

  /**
   * Check if a node type supports provider binding
   */
  const supportsBinding = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    return nodeDef?.supports_binding || false;
  }, [getNodeType]);

  /**
   * Check if a node type supports data mapping
   */
  const supportsMapping = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    return nodeDef?.supports_mapping || false;
  }, [getNodeType]);

  /**
   * Check if a node type can have side effects
   */
  const canHaveSideEffects = useCallback((typeId) => {
    const nodeDef = getNodeType(typeId);
    return nodeDef?.can_have_side_effects || false;
  }, [getNodeType]);

  return {
    nodeTypes,
    categories,
    loading,
    error,
    refetch: () => fetchRegistry(true),
    getNodeType,
    getSimpleFields,
    getAdvancedFields,
    getParamsSchema,
    supportsBinding,
    supportsMapping,
    canHaveSideEffects
  };
};

/**
 * Map node type to display category
 * Handles both legacy types (inputSource, capability, outputResult) and new v2 types (input.text, ai.summarization)
 */
export const getNodeCategory = (nodeType) => {
  if (!nodeType) return 'unknown';
  
  // Handle new v2 types (e.g., input.text, ai.summarization, logic.condition)
  if (nodeType.includes('.')) {
    const prefix = nodeType.split('.')[0];
    const categoryMap = {
      'trigger': 'triggers',
      'input': 'inputs',
      'voice': 'voice',
      'capability': 'capabilities',
      'ai': 'ai',
      'action': 'actions',
      'logic': 'logic',
      'output': 'outputs',
      'transform': 'transform',
      'error': 'error_handling',
      'integration': 'integrations',
      'agent': 'agents'
    };
    return categoryMap[prefix] || 'unknown';
  }
  
  // Handle legacy node types
  const legacyMap = {
    // Legacy React Flow node types
    'inputSource': 'inputs',
    'capability': 'capabilities',
    'outputResult': 'outputs',
    'trigger': 'triggers',
    // Common input type names
    'text': 'inputs',
    'file': 'inputs',
    'pdf': 'inputs',
    'docx': 'inputs',
    'doc': 'inputs',
    'image': 'inputs',
    'audio': 'inputs',
    'webhook': 'inputs',
    // Common capability names
    'summarization': 'ai',
    'extraction': 'ai',
    'classification': 'ai',
    'translation': 'ai',
    'sentiment': 'ai',
    'analyze': 'ai',
    'generate': 'ai',
    // Common output types
    'display': 'outputs',
    'response': 'outputs',
    'notification': 'outputs'
  };
  
  // Check exact match first
  if (legacyMap[nodeType]) {
    return legacyMap[nodeType];
  }
  
  // Check if nodeType contains any of the legacy keys (case-insensitive)
  const lowerType = nodeType.toLowerCase();
  for (const [key, category] of Object.entries(legacyMap)) {
    if (lowerType.includes(key.toLowerCase())) {
      return category;
    }
  }
  
  return 'unknown';
};

/**
 * Get color config for a category
 */
export const getCategoryColors = (category) => {
  const colorMap = {
    triggers: { color: 'text-pink-400', bgColor: 'bg-pink-500/10', borderColor: 'border-pink-500/20' },
    inputs: { color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
    voice: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
    capabilities: { color: 'text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/20' },
    ai: { color: 'text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/20' },
    agents: { color: 'text-indigo-400', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/20' },
    actions: { color: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/20' },
    logic: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
    outputs: { color: 'text-teal-400', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/20' },
    transform: { color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/20' },
    error_handling: { color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20' },
    integrations: { color: 'text-fuchsia-400', bgColor: 'bg-fuchsia-500/10', borderColor: 'border-fuchsia-500/20' }
  };
  return colorMap[category] || { color: 'text-slate-400', bgColor: 'bg-slate-500/20', borderColor: 'border-slate-500/30' };
};

/**
 * Map param type to input component type
 */
export const getInputType = (paramSchema) => {
  const typeMap = {
    'string': 'text',
    'number': 'number',
    'integer': 'number',
    'boolean': 'checkbox',
    'select': 'select',
    'multi_select': 'multi-select',
    'textarea': 'textarea',
    'code': 'code',
    'json': 'json',
    'file': 'file',
    'url': 'url',
    'email': 'email'
  };
  return typeMap[paramSchema?.type] || 'text';
};

/**
 * Check if a param should be visible based on conditional visibility
 */
export const shouldShowParam = (paramSchema, currentParams) => {
  if (!paramSchema?.show_if) return true;
  
  for (const [key, value] of Object.entries(paramSchema.show_if)) {
    const currentValue = currentParams[key];
    
    // Handle array of allowed values
    if (Array.isArray(value)) {
      if (!value.includes(currentValue)) return false;
    } else {
      if (currentValue !== value) return false;
    }
  }
  
  return true;
};

export default useNodeTypeRegistry;
